import { useStaticQuery, graphql } from 'gatsby';
import React, { useContext } from 'react';
import { useState } from 'react';

import useResponsive from '../../hooks/useResponsive';
import Testimonial from '../Testimonial/Testimonial';
import TestimonialCarousel from '../TestimonialCarousel/TestimonialCarousel';
import Button, { themes as buttonThemes, sizes } from '../UI/Button/Button';
import { GridReveal, GridItem } from '../UI/Grid/Grid';
import Section, { SectionContext, themes } from '../UI/Section/Section';
import * as styles from './Testimonials.module.scss';

const TESTIMONIALS_QUERY = graphql`
  query TestimonialsQuery {
    wp {
      acfOptionsCommonBlocks {
        common {
          featuredTestimonials {
            itemsPatient {
              ...Testimonial
            }
            itemsConsultant {
              ...Testimonial
            }
          }
        }
      }
    }

    allPatientTestimonials: wpTestimonialCategory(name: {eq: "Patient"}) {
      testimonials {
        nodes {
          ...Testimonial
        }
      }
    }

    allConsultantTestimonials: wpTestimonialCategory(name: {eq: "Consultant"}) {
      testimonials {
        nodes {
          ...Testimonial
        }
      }
    }
  }
`;

const SectionContent = ({ items, showAsCarousel, limit = 8 }) => {
  const { isWhiteOut } = useContext(SectionContext);
  const { isLarge } = useResponsive();
  const [ filteredItems, setFilteredItems ] = useState(items.slice(0, limit));

  return !isLarge || showAsCarousel ? (
    <TestimonialCarousel isWhiteOut={isWhiteOut}>
      {items.map((item) => (
        <Testimonial
          isPrimary
          isWhiteOut={isWhiteOut}
          {...item}
          key={item.title}
        />
      ))}
    </TestimonialCarousel>
  ) : (
    <>
      <GridReveal className={styles.grid}>
        {filteredItems.map((item, index) => (
          <GridItem className={styles.gridItem} key={index} md={6}>
            <Testimonial isWhiteOut={isWhiteOut} {...item} key={item.title} />
          </GridItem>
        ))}
      </GridReveal>

      {filteredItems.length !== items.length && (
        <div className={styles.footer}>
          <Button
            theme={buttonThemes.LINK}
            onClick={() => setFilteredItems(items)}
            isReverse={isWhiteOut}
            size={sizes.LARGE}
          >
            View more testimonials
          </Button>
        </div>
      )}
    </>
  );
};

const FeaturedTestimonials = ({
  theme,
  nodes = [],
  showAsCarousel = true,
  featuredOnly = true,
  categoryName = 'Patient', 
  ...other
}) => {
  const {
    wp: {
      acfOptionsCommonBlocks: {
        common: {
          featuredTestimonials: { 
            itemsPatient, 
            itemsConsultant
          },
        },
      },
    },
    allPatientTestimonials,
    allConsultantTestimonials
  } = useStaticQuery(TESTIMONIALS_QUERY);
  let items;

  if(!nodes.length) {
    if(featuredOnly || showAsCarousel) {
      items = categoryName === 'Patient' ? itemsPatient : itemsConsultant;
    } else {
      items = categoryName === 'Patient' ? 
        allPatientTestimonials.testimonials.nodes : 
        allConsultantTestimonials.testimonials.nodes;
    }
  } else {
    items = nodes;
  }
  
  return !items ? null : (
    <Section
      as="aside"
      id="testimonials"
      heading={categoryName ? `${categoryName} **testimonials**` : `**Testimonials**`}
      headingProps={{ isCentered: true }}
      theme={themes[ theme ]}
      {...other}
    >
      <SectionContent
        items={items}
        showAsCarousel={!!showAsCarousel}
      />
    </Section>
  );
};

export default FeaturedTestimonials;
